/**
 * Workspaces Redux Slice
 *
 * Manages workspace list state, filters, and selected workspace.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Workspace } from '../../apis/api-client';

export interface WorkspacesState {
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  loading: boolean;
  error: string | null;
  filters: {
    searchQuery: string;
  };
}

const initialState: WorkspacesState = {
  workspaces: [],
  selectedWorkspace: null,
  loading: false,
  error: null,
  filters: {
    searchQuery: '',
  },
};

export const workspacesSlice = createSlice({
  name: 'workspaces',
  initialState,
  reducers: {
    setWorkspaces: (state, action: PayloadAction<Workspace[]>) => {
      state.workspaces = action.payload;
      state.loading = false;
      state.error = null;
    },
    addWorkspace: (state, action: PayloadAction<Workspace>) => {
      state.workspaces.push(action.payload);
    },
    updateWorkspace: (state, action: PayloadAction<Workspace>) => {
      const index = state.workspaces.findIndex((w) => w.id === action.payload.id);
      if (index !== -1) {
        state.workspaces[index] = action.payload;
      }
    },
    removeWorkspace: (state, action: PayloadAction<number>) => {
      state.workspaces = state.workspaces.filter((w) => w.id !== action.payload);
    },
    setSelectedWorkspace: (state, action: PayloadAction<Workspace | null>) => {
      state.selectedWorkspace = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setFilters: (
      state,
      action: PayloadAction<Partial<WorkspacesState['filters']>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
});

export const {
  setWorkspaces,
  addWorkspace,
  updateWorkspace,
  removeWorkspace,
  setSelectedWorkspace,
  setLoading,
  setError,
  setFilters,
  resetFilters,
} = workspacesSlice.actions;

export default workspacesSlice.reducer;
